declare const bootstrap: any;

import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Subscription } from "rxjs";

import { QRCodeComponent } from "angularx-qrcode";

// Shared layout
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";

// Services (BLL / Core)
import { TicketsBllService } from "../tickets-bll.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { HomeBllService } from "../../home/home-bll.service";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { UserService } from "../../../core/services/user.service";
import { LanguageService } from "../../../core/services/language.service";
import { DeviceTypeService } from "../../../core/services/device-type.service";

// Models
import {
  InitiateUpgradePaymentResModel,
  LANG_MAP,
  RazorpayUpgradeRequest,
  TicketModel,
  VerifyUpgradePaymentRequest,
} from "../tickets.model";
import {
  failurePaymentUpgradeResultModel,
  ticketColorsModal,
  ticketsImagesBeforeSelecteingc,
} from "../../ticket-booking/ticket-booking.model";
import { UserEventStatusRespModel } from "../../profile/profile.model";
import {
  APIErrorModel,
  BaseResponse,
} from "../../../shared/models/base-response.model";
import { EventDetailsResponseModel } from "../../home/home.model";

/**
 * @description
 * The `TicketDetailsComponent` is responsible for displaying detailed information about a specific ticket.
 * It includes features like fetching ticket details, displaying QR codes, downloading tickets as images,
 * and sharing tickets via WhatsApp.
 */
@Component({
  selector: "app-ticket-details",
  imports: [
    LeftSideNavComponent,
    HeaderComponent,
    QRCodeComponent,
    CommonModule,
    FormsModule,
    LoaderComponent,
  ],
  templateUrl: "./ticket-details.component.html",
  styleUrl: "./ticket-details.component.scss",
})
export class TicketDetailsComponent implements OnInit, OnDestroy {
  // ---------------------------------------------------------------------------
  // Static assets / UI config
  // ---------------------------------------------------------------------------

  /**
   * Ticket images (black/color/white) shown before user selects ticket type.
   * Keyed by ticket code/type.
   */
  ticketsImagesBeforeSelecteingc: {
    [key: string]: { black: string; color: string; white: string };
  } = ticketsImagesBeforeSelecteingc;

  /**
   * Color themes per ticket type.
   * Used for gradient backgrounds, borders, QR code theme, etc.
   */
  ticketColorsModal: {
    [key: string]: {
      primary: string;
      darkPrimary: string;
      secondary: string;
      border: string;
      qrCode: string;
      ticketDetailsColorGradientBackground: string;
    };
  } = ticketColorsModal;

  // ---------------------------------------------------------------------------
  // Ticket state
  // ---------------------------------------------------------------------------

  /** Ticket code received from query param `code` */
  ticketCode: string = "";

  /** Ticket details fetched from backend */
  ticketDetails!: TicketModel;

  /** Shows a temporary invite-success banner (standard_invite_by_exh only) */
  showInviteSuccess: boolean = false;

  /** Current selected upgrade option (from upgrade_register_types) */
  public selectedUpgrade: any | null = null;

  /** TrackBy function for upgrade list rendering */
  public trackByCode = (_: number, u: any) => u.code;

  /** Called when user selects an upgrade option */
  public onSelectUpgrade(opt: any): void {
    this.selectedUpgrade = opt;
  }

  private inviteMessage: string = "KISAN Agri Show Invite";
  inviteMsgTitle: string = "";

  /** Used for any epoch comparisons / UI triggers */
  epoch_time: number = 0;

  userProfile: UserEventStatusRespModel["user_profile"] | null = null;

  /** (Optional) Local flags if you later want separate spinners */
  isTicketLoading = false;
  isUserStatusLoading = false;

  /** Wrapper element containing QR + ticket layout (used for download/share) */
  @ViewChild("qrContainer", { static: false }) qrContainer!: ElementRef;

  /**
   * Modal ref (template element)
   * NOTE: You actually open modal by id ("selecttickettypeModal") in openModalStatic().
   * This ref is still okay for template access or future use.
   */
  @ViewChild("upgradeModal", { static: true })
  upgradeModalRef!: ElementRef<HTMLElement>;

  private langSub!: Subscription;

  /** Source = Web / Android / iOS etc (from DeviceTypeService) */
  source: string = "";

  /** Event toggles */
  isVotingActive: boolean = false;
  isContestActive: boolean = false;

  /** Event details from Home service */
  eventDetails: EventDetailsResponseModel | null = null;

  /**
   * Controls which UI shows inside the upgrade modal:
   * - true  -> show payment-in-progress UI
   * - false -> show upgrade selection UI
   */
  isPaymentInProgress = false;

  /** Data shown in payment-in-progress UI */
  paymentAmount: number = 0;
  paymentOrderId: string = "";

  // ---------------------------------------------------------------------------
  // ✅ 409 polling state
  // ---------------------------------------------------------------------------

  /** Polling timer id */
  private ticketPollTimer: any = null;

  /** Last known ticket type code to compare with updated response */
  private lastTicketTypeCode: string = "";

  /** Avoid double-starting polling */
  private isPollingTicketDetails = false;

  constructor(
    private bll: TicketsBllService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loader: LoaderService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private languageService: LanguageService,
    private deviceService: DeviceTypeService,
    private homeBll: HomeBllService
  ) { }

  /**
   * Initializes:
   * - Ticket details (if code exists)
   * - User event status (profile & counts)
   * - Payment success modal display (from sessionStorage flag)
   * - Face-api models
   * - Opens upgrade modal if imgUploaded=true query param
   * - Loads event details and sets menu flags
   */
  async ngOnInit(): Promise<void> {
    this.loader.show();
    const code = this.getTicketCode();

    try {
      const promises: Promise<void>[] = [];

      if (code) {
        promises.push(this.getTicketDetails(code));
      }
      promises.push(this.fetchUserEventStatus());

      await Promise.all(promises);
    } catch (error) {
      console.error("Error during init:", error);
    } finally {
      this.loader.hide();
    }

    // Used by payment-success trigger + any other UI timing
    this.epoch_time = Date.now();

    // If payment was successful, show payment-success modal after page reload
    setTimeout(() => this.maybeShowPaymentSuccess(), 200);

    // Identify user's platform/source
    this.source = this.deviceService.getUserSource();
    // await this.bll.loadModels();

    // If user returned from upload-photo route, open upgrade modal
    const imgUploadedParam =
      this.activatedRoute.snapshot.queryParams["imgUploaded"] || "";
    if (imgUploadedParam === "true") {
      // Clear query param (clean URL)
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { imgUploaded: null },
        queryParamsHandling: "merge",
      });

      // Open upgrade UI (not payment-in-progress)
      this.openUpgradeModal();
    }

    // Fetch event details and configure menu toggles
    this.loadEventDetails();
  }

  ngOnDestroy(): void {
    if (this.langSub) this.langSub.unsubscribe();
    this.stopTicketDetailsPolling();
  }

  /**
   * Opens the bootstrap modal with:
   * - backdrop: static (outside click won't close)
   * - keyboard: false (ESC won't close)
   */
  private openModalStatic(): void {
    const el = document.getElementById("selecttickettypeModal");
    if (!el) return;

    const modal =
      bootstrap.Modal.getInstance(el) ||
      new bootstrap.Modal(el, { backdrop: "static", keyboard: false });

    modal.show();
  }

  /** Show upgrade UI inside the modal */
  private openUpgradeModal(): void {
    // ✅ show upgrade selection UI (so user can pick)
    this.isPaymentInProgress = false;
    this.openModalStatic();
  }

  /**
   * Show payment-in-progress UI inside the modal.
   * It also updates:
   * - paymentAmount
   * - paymentOrderId
   */
  private openPaymentInProgressModal(amount?: number, orderId?: string): void {
    this.isPaymentInProgress = true;
    this.paymentAmount = Number(amount ?? this.amountToPay ?? 0);
    this.paymentOrderId = orderId ?? this.paymentOrderId ?? "";
    this.openModalStatic();
  }

  /** When modal is closed, reset UI state to upgrade selection */
  onCloseUpgradeModal(): void {
    this.isPaymentInProgress = false;
    this.stopTicketDetailsPolling();
  }

  // ---------------------------------------------------------------------------
  // ✅ 409 polling helpers
  // ---------------------------------------------------------------------------

  /**
   * Starts polling ticket details every 10 seconds after 409.
   * Stops when ticket_type_code changes (upgrade finished on backend).
   */
  private startTicketDetailsPolling(): void {
    if (this.isPollingTicketDetails) return;

    this.isPollingTicketDetails = true;

    // store old ticket type code before polling starts
    this.lastTicketTypeCode = this.ticketDetails?.ticket_type_code || "";

    this.ticketPollTimer = setInterval(async () => {
      try {
        const code = this.getTicketCode();
        if (!code) return;

        // call details API
        const res = await this.bll.getTicketDetails(code);
        const newDetails: TicketModel = res?.data;

        const newType = newDetails?.ticket_type_code || "";

        // If upgraded type is different -> update UI and stop polling
        if (
          newType &&
          this.lastTicketTypeCode &&
          newType !== this.lastTicketTypeCode
        ) {
          this.stopTicketDetailsPolling();

          // Update UI state
          this.ticketDetails = newDetails;

          // Re-init upgrades selection if needed
          await this.initUpgradesSelection();

          // Close payment-in-progress UI
          this.isPaymentInProgress = false;

          // Optional: close modal
          const el = document.getElementById("selecttickettypeModal");
          if (el) {
            const modal = bootstrap.Modal.getInstance(el);
            modal?.hide();
          }

          return;
        }
      } catch (e) {
        console.warn("Polling ticket details failed, will retry:", e);
      }
    }, 10_000);
  }

  /** Stops polling ticket details */
  private stopTicketDetailsPolling(): void {
    if (this.ticketPollTimer) {
      clearInterval(this.ticketPollTimer);
      this.ticketPollTimer = null;
    }
    this.isPollingTicketDetails = false;
  }

  // ---------------------------------------------------------------------------
  // Small UI helpers
  // ---------------------------------------------------------------------------

  /** Show temporary invite accepted banner */
  showSuccessMessage(): void {
    if (this.ticketDetails?.ticket_type_code === "standard_invite_by_exh") {
      this.showInviteSuccess = true;

      setTimeout(() => {
        this.showInviteSuccess = false;
        this.bll.clearInviteAccepted();
      }, 3000);
    }
  }

  goBack(): void {
    this.router.navigate(["reg/my-tickets"]);
  }

  // ---------------------------------------------------------------------------
  // Routing / Params
  // ---------------------------------------------------------------------------

  /** Reads ticket code from query param `code` */
  getTicketCode(): string {
    return this.activatedRoute.snapshot.queryParams["code"] || "";
  }

  /** Global loader state (from LoaderService) */
  get isLoading(): boolean {
    return this.loader.isShown;
  }

  // ---------------------------------------------------------------------------
  // API calls: Ticket details + User status
  // ---------------------------------------------------------------------------

  /**
   * Fetch ticket details using ticketCode.
   * - On 410: navigates to /home (expired/invalid link)
   */
  getTicketDetails(ticketCode: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.bll
        .getTicketDetails(ticketCode)
        .then((res) => {
          this.handleTicketDetailsResponse(res);
          resolve();
        })
        .catch((err: APIErrorModel) => {
          console.error(err);

          if (err.status === 410) {
            this.router.navigate(["/home"], { replaceUrl: true });
          }

          reject(err);
        });
    });
  }

  /**
   * Fetch user event status:
   * - sets userProfile
   * - updates UserService with user data + ticket counts
   */
  fetchUserEventStatus(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.profileBll
        .submitGetUserEventStatus()
        .then((res: BaseResponse<UserEventStatusRespModel>) => {
          this.userProfile = res.data.user_profile;

          if (this.userProfile) {
            const previewLogo =
              this.userProfile.image_url ??
              "/assets/images/Default-profile-photo.png";

            const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;

            this.userService.setUser({
              name: userName,
              profileImg: previewLogo,
              mobile: this.userProfile.mobile,
              state: this.userProfile.state,
              city: this.userProfile.city,
              hasUnassignedTickets:
                res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
              hasAssignedTickets:
                res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 ||
                res.data.event_my_tickets.tickets_assigned_for_me > 0,
            });
          }

          resolve();
        })
        .catch((err: APIErrorModel) => {
          console.error("Error fetching user event status:", err);
          reject(err);
        });
    });
  }

  /**
   * Handles ticket details response:
   * - sets ticketDetails
   * - initializes upgrade selection
   * - updates ticketCode value (pass_code or ticket_code)
   * - subscribes language changes (for invite message)
   * - auto-download ticket image once (if eligible)
   */
  async handleTicketDetailsResponse(res: any): Promise<void> {
    if (!res) {
      console.error("Error fetching ticket details");
      return;
    }

    this.ticketDetails = res.data;

    // Ensure upgrade selection has default
    await this.initUpgradesSelection();

    // Show invite-success if applicable
    this.showSuccessMessage();

    // Prefer pass_code, fallback to ticket_code
    this.ticketCode = String(
      this.ticketDetails?.pass_code || this.ticketDetails?.ticket_code
    );

    // Update invite message whenever language changes
    this.langSub = this.languageService.language$.subscribe((lang) => {
      this.updateInviteMessageByLang(lang);
    });

    // Auto-download ticket once per session (if eligible)
    if (
      !this.ticketDetails.is_entry_expired &&
      !this.ticketDetails.badge_created_datetime
    ) {
      setTimeout(() => {
        const key = `downloaded_${this.ticketCode}`;
        if (!sessionStorage.getItem(key)) {
          this.downloadAsImage();
          sessionStorage.setItem(key, "true");
        }
      }, 3000);
    }
  }

  // ---------------------------------------------------------------------------
  // Upgrade helpers
  // ---------------------------------------------------------------------------

  /** True if upgrades are available to show */
  public get canUpgrade(): boolean {
    return !!this.ticketDetails && this.visibleUpgrades.length > 0;
  }

  /**
   * Visible upgrades:
   * - filters out free upgrades (paid_amount <= 0)
   * - removes duplicates by code
   */
  get visibleUpgrades(): any[] {
    const serverList: any[] =
      (this.ticketDetails as any)?.upgrade_register_types ?? [];
    if (!Array.isArray(serverList) || serverList.length === 0) return [];

    const filtered = serverList.filter((u) => Number(u.paid_amount) > 0);
    return Array.from(new Map(filtered.map((u) => [u?.code, u])).values());
  }

  /** MRP shown in UI (display price) */
  public get mrpTotal(): number {
    return this.selectedUpgrade?.display_price;
  }

  /**
   * All upgrades from server (unique by code).
   * This includes upgrades that might be free as well.
   */
  private get serverUpgrades(): any[] {
    const list: any[] =
      (this.ticketDetails as any)?.upgrade_register_types ?? [];
    return Array.from(new Map(list.map((u) => [u?.code, u])).values());
  }

  /** Selects first upgrade by default */
  private initUpgradesSelection(): void {
    if (!this.selectedUpgrade) {
      const visible = this.visibleUpgrades;
      if (Array.isArray(visible) && visible.length > 0) {
        this.selectedUpgrade = visible[0];
      } else {
        this.selectedUpgrade = this.serverUpgrades[0] ?? null;
      }
    }
  }

  /** Total discount applied by backend */
  public get totalDiscounts(): number {
    const v = this.selectedUpgrade?.applied_total_discount ?? 0;
    return Number(v) || 0;
  }

  /** Amount user needs to pay */
  public get amountToPay(): number {
    return Number(this.selectedUpgrade?.paid_amount ?? 0);
  }

  // ---------------------------------------------------------------------------
  // Ticket actions: download / share / invite
  // ---------------------------------------------------------------------------

  /** Downloads ticket as image (uses BLL to create snapshot) */
  downloadAsImage(): void {
    if (this.qrContainer?.nativeElement) {
      this.bll.handleTicketAction(
        this.qrContainer.nativeElement,
        this.ticketCode,
        "download"
      );
    }
  }

  /** Shares ticket on WhatsApp (uses same snapshot logic) */
  shareOnWhatsApp(): void {
    this.bll.handleTicketAction(
      this.qrContainer.nativeElement,
      this.ticketCode,
      "share"
    );
  }

  /**
   * Invite friends:
   * - uses Web Share API if available
   * - otherwise copies invite message to clipboard
   */
  async inviteFriends(): Promise<void> {
    if (navigator.share) {
      navigator
        .share({
          title: this.inviteMsgTitle,
          text: this.inviteMessage,
        })
        .catch((err) => console.error("❌ Sharing failed", err));
    } else {
      this.bll.copyToClipboard(this.inviteMessage);
    }
  }

  // ---------------------------------------------------------------------------
  // Upgrade payment flow: Razorpay
  // ---------------------------------------------------------------------------

  /**
   * Opens Razorpay popup and verifies payment on success.
   * On failure: calls failure API with available Razorpay metadata.
   */
  async openPayment(data: InitiateUpgradePaymentResModel): Promise<void> {
    const encodedCode = encodeURIComponent(this.ticketCode);
    const mappedEncodedCode = btoa(encodedCode);

    this.loader.show();

    this.bll.payWithRazor(
      `${data.first_name} ${data.last_name}`,
      data.mobile,
      data,

      // ✅ Razorpay Success Callback
      (response) => {
        this.loader.show();

        // Close all open modals (defensive)
        document.querySelectorAll(".modal.show").forEach((m) => {
          (bootstrap.Modal.getInstance(m) || new bootstrap.Modal(m)).hide();
        });
        document
          .querySelectorAll(".modal-backdrop")
          ?.forEach((b) => b.remove());

        // Clean up body classes/styles bootstrap adds
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");

        // Build verify request for backend
        const verifyData: VerifyUpgradePaymentRequest = {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          summary_id: data.summary_id,
          language: this.profileBll.getSelectedLanguage(),
          source: this.source,
        };

        // Verify payment on backend
        this.bll
          .verifyUpgradePayment(mappedEncodedCode, verifyData)
          .then(() => {
            this.onPaymentSuccess();
          })
          .catch((err) => {
            this.loader.hide();
            console.error("Payment verification failed:", err);
          });
      },

      // ❌ Razorpay Failure / Dismiss Callback
      (error) => {
        this.loader.hide();

        // User closed Razorpay popup manually (do not call failure API)
        if (error?.error === "Payment popup closed.") {
          console.warn("Payment dismissed by user.");
          return;
        }

        /**
         * Razorpay error payload commonly looks like:
         * error.error = {
         *   code, description, metadata: { order_id, payment_id }
         * }
         */
        const rzpErr = error?.error ?? {};
        const meta = rzpErr?.metadata ?? {};

        const payload: failurePaymentUpgradeResultModel = {
          razorpay_order_id: meta?.order_id || data?.id || "",
          razorpay_payment_id: meta?.payment_id || "",
          error_code: rzpErr?.code || "",
          error_description: rzpErr?.description || "",
          summary_id: data.summary_id || "",
        };

        const encodedCode2 = encodeURIComponent(this.ticketCode);
        const mappedEncodedCode2 = btoa(encodedCode2);

        this.paymentFailed(payload, mappedEncodedCode2);
        console.error("Payment failed:", error);
      }
    );
  }

  /**
   * Calls failure API to store payment failure on backend.
   * Useful for analytics and retry flows.
   */
  private paymentFailed(
    payload: failurePaymentUpgradeResultModel,
    ticketCode: string
  ): void {
    this.bll
      .failurePaymentUpgrade(payload, ticketCode)
      .then((response) => {
        console.log("Payment failure recorded:", response);
      })
      .catch((error) => {
        console.error("Error recording payment failure:", error);
      });
  }

  /**
   * On successful verify:
   * - Store discount in sessionStorage (to show in success modal after reload)
   * - Store "showPaymentSuccess" flag
   * - Reload page to refresh ticket details/status
   */
  onPaymentSuccess(): void {
    this.stopTicketDetailsPolling(); // ✅ stop if running
    this.loader.show();

    const discount = this.selectedUpgrade?.applied_total_discount ?? 0;
    sessionStorage.setItem("savedDiscountAmount", discount.toString());
    sessionStorage.setItem("showPaymentSuccess", "1");

    setTimeout(() => window.location.reload(), 300);
  }

  /**
   * After reload, this checks sessionStorage and shows payment success modal:
   * - reads saved discount amount
   * - updates DOM span in modal
   * - auto closes modal after 3 seconds
   */
  private maybeShowPaymentSuccess(): void {
    const shouldShow = sessionStorage.getItem("showPaymentSuccess") === "1";
    if (!shouldShow) return;

    sessionStorage.removeItem("showPaymentSuccess");

    const savedDiscount = sessionStorage.getItem("savedDiscountAmount") || "0";

    const discountSpan = document.querySelector(
      "#paymentSuccessModal .amt"
    ) as HTMLElement;

    if (discountSpan) discountSpan.textContent = `₹${savedDiscount}`;

    sessionStorage.removeItem("savedDiscountAmount");

    const el = document.getElementById("paymentSuccessModal");
    if (!el) return;

    const modal =
      bootstrap.Modal.getInstance(el) ||
      new bootstrap.Modal(el, { backdrop: "static", keyboard: false });

    modal.show();
    setTimeout(() => modal.hide(), 3000);
  }

  /**
   * ✅ Pay button handler
   *
   * Flow:
   * 1) initiateUpgradePayment()
   *    - 200: open Razorpay
   *    - 409: show "Payment in progress" UI inside modal
   *           + poll ticket details every 10 sec until ticket type changes
   */
  onPayHandler(): void {
    // 🔒 Guard: no upgrade selected
    if (!this.selectedUpgrade?.code) {
      console.warn("No upgrade selected. Blocking Pay.");
      return;
    }
    const encodedCode = encodeURIComponent(this.ticketCode);
    const mappedEncodedCode = btoa(encodedCode);

    const data: RazorpayUpgradeRequest = {
      new_register_type_code: this.selectedUpgrade.code,
      language: this.profileBll.getSelectedLanguage(),
      source: this.source,
    };

    this.loader.show();

    this.bll
      .initiateUpgradePayment(mappedEncodedCode, data)
      .then((res) => {
        // ✅ Payment initiation success -> open Razorpay
        this.isPaymentInProgress = false;
        this.openPayment(res.data);

      })
      .catch((err: APIErrorModel) => {
        if (err?.status === 409) {
          const orderId =
            (err?.data as any)?.razorpay_order_id ||
            (err?.data as any)?.order_id ||
            (err?.data as any)?.id ||
            "";

          const rawAmountPaise = (err?.data as any)?.amount;
          const rawAmountRupees =
            (err?.data as any)?.paid_amount ?? this.amountToPay;
          const normalizedAmount =
            rawAmountPaise != null && rawAmountPaise !== undefined
              ? Number(rawAmountPaise) / 100
              : Number(rawAmountRupees ?? 0);
          this.paymentAmount = Number(normalizedAmount);
          this.paymentOrderId = String(orderId ?? "");
          this.openPaymentInProgressModal(normalizedAmount, orderId);


          // ✅ Start polling ticket details every 10 sec
          this.startTicketDetailsPolling();
          return;
        }
      })
      .finally(() => {
        this.loader.hide();
      });
  }

  // ---------------------------------------------------------------------------
  // Language / invite message logic
  // ---------------------------------------------------------------------------

  /**
   * Updates invite title and invite message based on selected language.
   * - hi/mr -> Marathi/Hindi title
   * - else  -> English title
   *
   * Backend provides invite_friend array with (lang,text).
   * `LANG_MAP` maps language codes to display names used in invite data.
   */
  private updateInviteMessageByLang(selectedLang: string): void {
    if (selectedLang === "hi" || selectedLang === "mr") {
      this.inviteMsgTitle = "किसान कृषी प्रदर्शन आमंत्रण";
    } else {
      this.inviteMsgTitle = "KISAN Agri Show Invite";
    }

    const lang = LANG_MAP[selectedLang] || "Marathi";
    const inviteData = this.ticketDetails?.invite_friend ?? [];
    const matchingInvite = inviteData.find((invite) => invite.lang === lang);

    if (matchingInvite?.text) {
      const decoded = decodeURIComponent(matchingInvite.text);
      this.inviteMessage = decoded.replace(/\*/g, "");
    }
  }

  // ---------------------------------------------------------------------------
  // Misc helpers
  // ---------------------------------------------------------------------------

  /** Returns invite accepted flag from BLL (stored somewhere like storage/session) */
  getIsInviteAccepted(): string | null {
    return this.bll.isInviteAccepted();
  }

  /**
   * Upgrade ticket process:
   * - If no image uploaded, route user to upload-photo
   * - Otherwise open upgrade modal
   */
  async upgradeTicketProcess(): Promise<void> {
    const ticketImage = this.ticketDetails?.image_url;

    if (!ticketImage) {
      this.routeToUploadPhoto();
      return;
    }

    // If image exists → check face detection
    // const hasFace = await this.hasFacePhoto(ticketImage);

    // if (hasFace) {
    // Open modal
    // const el = document.getElementById("selecttickettypeModal");
    // if (el) {
    //   const modal =
    //     bootstrap.Modal.getInstance(el) ||
    //     new bootstrap.Modal(el, { backdrop: "static", keyboard: false });
    //   modal.show();
    // }
    // } else {
    // No face found → redirect
    //   this.routeToUploadPhoto();
    // }
    this.openUpgradeModal();
  }

  // ---------------------------------------------------------------------------
  // Navigation actions
  // ---------------------------------------------------------------------------

  goToDigitalCatalogue(): void {
    this.router.navigate(["catalogue/pavilion-list"]);
  }

  onClickVote(): void {
    this.router.navigate(["contests/participants-list"]);
  }

  /** Navigates to buy ticket flow */
  goToBuyTicket(): void {
    this.router.navigate(["reg/select-ticket"]);
  }

  /** Navigates to upload-photo flow, passing current ticket code */
  routeToUploadPhoto(): void {
    this.router.navigate(["reg/upload-photo"], {
      queryParams: { code: this.getTicketCode() },
    });
  }

  // ---------------------------------------------------------------------------
  // Event Details
  // ---------------------------------------------------------------------------

  /**
   * Fetches event details and sets:
   * - contest/voting flags
   * - left menu config in UserService
   */
  loadEventDetails(): void {
    this.homeBll
      .fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;

        this.isContestActive = this.eventDetails.is_contest_active;
        this.isVotingActive = this.eventDetails.is_voting_active;

        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status,
        });
      })
      .catch((error) => {
        this.isVotingActive = false;
        this.isContestActive = false;
        console.error("Error fetching event details:", error);
      });
  }

  // ---------------------------------------------------------------------------
  // Computed states
  // ---------------------------------------------------------------------------

  /**
   * True when:
   * - ticket not expired
   * - badge not created yet
   * Used to show "Download Ticket" button etc.
   */
  get isFirstDayTicketAndNotUsedAndNotExpired(): boolean {
    return (
      !this.ticketDetails?.is_entry_expired &&
      !this.ticketDetails?.badge_created_datetime
    );
  }
}
